import { SettingHeader } from '@affine/component/setting-components';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  type AvailableIntegrationsQuery,
  availableIntegrationsQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { LockIcon } from '@blocksuite/icons/rc';
import { cssVar } from '@streamlain/theme';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useService } from '@toeverything/infra';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { SubPageProvider, useSubPageIsland } from '../../sub-page';
import type { SettingState } from '../../types';
import {
  IntegrationCard,
  IntegrationCardContent,
  IntegrationCardFooter,
  IntegrationCardHeader,
} from './card';
import { getAllowedIntegrationList, getVerticalDefinitions } from './constants';
import { type IntegrationItem } from './constants';
import { list } from './index.css';
import { VerticalIntegrationTile } from './vertical-tile';

interface IntegrationAvailability {
  allowed: boolean;
  requiredPlan: string | null;
  visibility: string;
}

/**
 * Build the {featureKey -> id} map from the live INTEGRATION_LIST so the
 * source of truth is a single field on each card definition (DRY).
 */
function buildFeatureKeyMap(items: IntegrationItem[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    if (item.featureKey) {
      map.set(item.featureKey, item.id);
    }
  }
  return map;
}

function useAvailableIntegrations(items: IntegrationItem[]): {
  data: Map<string, IntegrationAvailability>;
  isLoaded: boolean;
} {
  const workspaceService = useService(WorkspaceService);
  const serverService = workspaceService.workspace.scope.get(
    WorkspaceServerService
  );
  const [data, setData] = useState<Map<string, IntegrationAvailability>>(
    new Map()
  );
  const [isLoaded, setIsLoaded] = useState(false);

  const featureKeyMap = useMemo(() => buildFeatureKeyMap(items), [items]);

  useEffect(() => {
    const server = serverService?.server;
    if (!server) {
      setIsLoaded(true);
      return;
    }

    let cancelled = false;
    server
      .gql({
        query: availableIntegrationsQuery,
        variables: { workspaceId: workspaceService.workspace.id },
      })
      .then((result: AvailableIntegrationsQuery) => {
        if (cancelled) return;
        const map = new Map<string, IntegrationAvailability>();
        for (const item of result.workspace.availableIntegrations) {
          const frontendId = featureKeyMap.get(item.featureKey);
          if (frontendId) {
            map.set(frontendId, {
              allowed: item.allowed,
              requiredPlan: item.requiredPlan ?? null,
              visibility: item.visibility ?? 'visible',
            });
          }
        }
        setData(map);
      })
      .catch(() => {
        // Silently ignore — integrations default to available
      })
      .finally(() => {
        if (!cancelled) setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [serverService, workspaceService.workspace.id, featureKeyMap]);

  return { data, isLoaded };
}

/**
 * Tab visibility hook — returns true when the integrations tab has at
 * least one card or vertical to render. Used by the workspace settings
 * sidebar to hide the tab entirely when there is nothing to show on the
 * user's workspace flavour (e.g. local web workspace where no cloud-only
 * integration applies).
 *
 * SOLID:
 * - S: Only computes integration tab visibility
 * - DRY: Reuses the same INTEGRATION_LIST + VERTICAL_DEFINITIONS sources
 *        the tab itself renders from.
 */
export function useHasAnyIntegration(): boolean {
  const workspaceService = useService(WorkspaceService);
  const isCloudWorkspace = workspaceService.workspace.flavour !== 'local';
  const integrationList = useMemo(
    () => getAllowedIntegrationList(isCloudWorkspace),
    [isCloudWorkspace]
  );
  const verticalDefs = useMemo(
    () => getVerticalDefinitions(isCloudWorkspace),
    [isCloudWorkspace]
  );
  const { features, isLoading } = useFeatureGate();

  if (!isCloudWorkspace) {
    return integrationList.length > 0 || verticalDefs.length > 0;
  }

  if (isLoading) return true;

  const featureMap = new Map(features.map(f => [f.featureKey, f]));

  const hasVisibleIntegration = integrationList.some(item => {
    if (!item.featureKey) return true; // no plan gate (e.g. Calendar) — always visible
    const f = featureMap.get(item.featureKey);
    return f !== undefined && f.visibility !== 'hidden';
  });

  const hasVisibleVertical = verticalDefs.some(def => {
    const f = featureMap.get(def.featureKey);
    return f !== undefined && f.visibility !== 'hidden';
  });

  return hasVisibleIntegration || hasVisibleVertical;
}

export const IntegrationSetting = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const [opened, setOpened] = useState<string | null>(null);
  const workspaceService = useService(WorkspaceService);
  const isCloudWorkspace = workspaceService.workspace.flavour !== 'local';

  const integrationList = useMemo(
    () => getAllowedIntegrationList(isCloudWorkspace),
    [isCloudWorkspace]
  );

  // sl-vw37.1.3: Vertical definitions for the integration list
  const verticalDefs = useMemo(
    () => getVerticalDefinitions(isCloudWorkspace),
    [isCloudWorkspace]
  );

  const { data: availability, isLoaded: integrationsLoaded } =
    useAvailableIntegrations(integrationList);

  const handleCardClick = useCallback(
    (card: IntegrationItem) => {
      // Block opening settings for locked integrations
      const status = availability.get(card.id);
      if (status && !status.allowed) return;

      if ('setting' in card && card.setting) {
        setOpened(card.id);
      }
    },
    [availability]
  );

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.integration.integrations']()}
        subtitle={<>{t['com.streamlain.integration.setting.description']()}</>}
      />
      <ul className={list}>
        {/* Wait for feature availability before rendering to prevent flash of hidden cards */}
        {isCloudWorkspace && !integrationsLoaded
          ? null
          : integrationList.map(item => {
              const status = availability.get(item.id);

              // sl-lrcr: Hidden features are always invisible
              if (status?.visibility === 'hidden') return null;

              const title =
                typeof item.name === 'string'
                  ? t[item.name]()
                  : t[item.name.i18nKey]();
              const desc =
                typeof item.desc === 'string'
                  ? t[item.desc]()
                  : t[item.desc.i18nKey]();

              const isLocked = status !== undefined && !status.allowed;

              return (
                <li key={item.id}>
                  <IntegrationCard
                    onClick={() => handleCardClick(item)}
                    link={'link' in item ? item.link : undefined}
                    style={
                      isLocked ? { opacity: 0.6, cursor: 'default' } : undefined
                    }
                  >
                    <IntegrationCardHeader icon={item.icon} title={title} />
                    <IntegrationCardContent desc={desc} />
                    {isLocked && (
                      <IntegrationCardFooter>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            onChangeSettingState?.({
                              activeTab: 'workspace:plans',
                            });
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: cssVar('fontXs'),
                            color: cssVarV2('text/secondary'),
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          <LockIcon style={{ fontSize: 14 }} />
                          Upgrade to {status.requiredPlan}
                        </button>
                      </IntegrationCardFooter>
                    )}
                  </IntegrationCard>

                  {'setting' in item && item.setting && !isLocked ? (
                    <IntegrationSettingPage
                      open={opened === item.id}
                      onClose={() => setOpened(null)}
                    >
                      {item.setting}
                    </IntegrationSettingPage>
                  ) : null}
                </li>
              );
            })}

        {/* sl-vw37.1.3: Vertical integration tiles (KB, News Portal, etc.) */}
        {verticalDefs.map(def => (
          <VerticalIntegrationTile key={def.id} definition={def} />
        ))}
      </ul>
    </>
  );
};

const IntegrationSettingPage = ({
  children,
  open,
  onClose,
}: {
  children: ReactNode;
  open: boolean;
  onClose: () => void;
}) => {
  const t = useI18n();
  const island = useSubPageIsland();

  if (!island) {
    return null;
  }

  return (
    <SubPageProvider
      backText={t['com.streamlain.integration.integrations']()}
      island={island}
      open={open}
      onClose={onClose}
    >
      {children}
    </SubPageProvider>
  );
};
